import type {
  CcpPlan,
  CcpSections,
  CompetitorPlan,
  CompetitorSections,
  FieldValue,
  PlanConfidence,
  PlanMeta,
  PlanSource,
} from "./types"

/**
 * Normalise a CCP / Competitor plan API payload into the frontend plan shape.
 *
 * The backend serialises plans nested (`{ meta, summary, sections, … }`), but this
 * accepts either that nested shape or a legacy flat payload (section keys at the top
 * level next to meta). Callers always receive the frontend contract, with defaults
 * filled for any missing meta so `planIsGenerated` and the meta header stay safe.
 */

type RawPlan = Record<string, unknown>

function asRecord(value: unknown): RawPlan {
  return value !== null && typeof value === "object" ? (value as RawPlan) : {}
}

function normalizeMeta(raw: RawPlan): PlanMeta {
  const meta = asRecord(raw.meta)
  // Prefer the nested meta value, falling back to a top-level (flat) key.
  const get = (key: string): unknown => meta[key] ?? raw[key]
  return {
    createdBy: get("createdBy") as string | undefined,
    createdAt: get("createdAt") as string | undefined,
    updatedAt: get("updatedAt") as string | undefined,
    generatedAt: (get("generatedAt") as string | null | undefined) ?? null,
    version: (get("version") as number | undefined) ?? 0,
    area: get("area") as string | undefined,
    confidence: get("confidence") as PlanConfidence | undefined,
    isFreshDraft: Boolean(meta.isFreshDraft),
  }
}

/** The object that holds the section fields — the nested `sections`, or the payload itself when flat. */
function sectionSource(raw: RawPlan): RawPlan {
  return raw.sections != null ? asRecord(raw.sections) : raw
}

function ccpSections(source: RawPlan): CcpSections {
  return {
    overview: source.overview as FieldValue,
    buying: source.buying as CcpSections["buying"],
    potential: source.potential as CcpSections["potential"],
    market: source.market as FieldValue,
    players: source.players as FieldValue,
    model: source.model as FieldValue,
    engage: source.engage as CcpSections["engage"],
    needs: source.needs as FieldValue,
    value: source.value as FieldValue,
    activities: source.activities as FieldValue,
  }
}

function competitorSections(source: RawPlan): CompetitorSections {
  return {
    overview: source.overview as FieldValue,
    businessModel: source.businessModel as FieldValue,
    marketOverview: source.marketOverview as FieldValue,
    keyActivities: source.keyActivities as FieldValue,
    strengthsWeaknesses: source.strengthsWeaknesses as CompetitorSections["strengthsWeaknesses"],
    nolatoRelationships: source.nolatoRelationships as FieldValue,
  }
}

function normalizeSources(raw: RawPlan): PlanSource[] {
  const sources = raw.sources
  return Array.isArray(sources) ? (sources as PlanSource[]) : []
}

export function normalizeCcpPlan(raw: unknown): CcpPlan {
  const r = asRecord(raw)
  return {
    segmentId: r.segmentId as string,
    companyId: r.companyId as string,
    companyName: r.companyName as string | undefined,
    segmentName: r.segmentName as string | undefined,
    meta: normalizeMeta(r),
    summary: r.summary as FieldValue,
    sections: ccpSections(sectionSource(r)),
    dataGaps: (r.dataGaps as string[] | null | undefined) ?? undefined,
    sources: normalizeSources(r),
  }
}

/**
 * Resolve the current value of a section field from a cached plan — used to fill the
 * review modal's Current column when an older update response omits `current`.
 */
export function resolveCcpCurrent(plan: CcpPlan | null | undefined, fieldKey: string): unknown {
  if (!plan) return null
  if (fieldKey === "summary") return plan.summary
  return (plan.sections as Record<string, unknown>)[fieldKey] ?? null
}

export function resolveCompetitorCurrent(
  plan: CompetitorPlan | null | undefined,
  fieldKey: string
): unknown {
  if (!plan) return null
  if (fieldKey === "summary") return plan.summary ?? null
  return (plan.sections as Record<string, unknown>)[fieldKey] ?? null
}

export function normalizeCompetitorPlan(raw: unknown): CompetitorPlan {
  const r = asRecord(raw)
  return {
    segmentId: r.segmentId as string,
    companyId: r.companyId as string,
    companyName: r.companyName as string | undefined,
    segmentName: r.segmentName as string | undefined,
    meta: normalizeMeta(r),
    summary: r.summary as FieldValue | undefined,
    sections: competitorSections(sectionSource(r)),
    dataGaps: (r.dataGaps as string[] | null | undefined) ?? undefined,
    sources: normalizeSources(r),
  }
}
